import { useState } from "react";
import { Check, X } from "lucide-react";
import { LGoButton } from "@/ui/branded";
import { CustomFieldAnswers } from "@/features/custom-fields/components/CustomFieldAnswers";
import type { CustomFieldAnswer } from "@/domain/custom-fields/types";
import { confirmationDeadlineStatus } from "@/domain/reservation/rules";
import type { PartnerReservation } from "@/domain/reservation/types";
import { czechPlural } from "@/lib/czech";
import { formatDateTime } from "@/lib/time";
import { formatPrice } from "@/lib/money";
import { ConfirmationDeadline } from "./ConfirmationDeadline";
import { RejectReservationDialog } from "./RejectReservationDialog";

interface ReservationQueueRowProps {
  reservation: PartnerReservation;
  onConfirm: (reservationId: number) => void;
  onReject: (reservationId: number, options: { onSettled: () => void }) => void;
  isAnswering: boolean;
}

const personForms = { one: "osoba", few: "osoby", many: "osob" };

const headcountNouns = ["osob", "lid", "host", "ucastn"];

function withoutDiacritics(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** Every custom field is named by the business that asked it, so a headcount can
 *  only be recognised from how the question reads — never assumed. A booking for
 *  two into a room with seven places is a different answer than one for eight,
 *  which is why the number that was asked for leads the row and the rest stay
 *  where they render. */
function headcountOf(answer: CustomFieldAnswer): number | null {
  const count = Number(answer.value);
  if (!Number.isInteger(count) || count <= 0) return null;
  const label = withoutDiacritics(answer.label);
  const countsSomething = label.includes("pocet") || label.includes("kolik");
  const countsPeople = headcountNouns.some((noun) => label.includes(noun));
  return countsSomething && countsPeople ? count : null;
}

function splitOffHeadcount(answers: CustomFieldAnswer[]): {
  headcount: number | null;
  otherAnswers: CustomFieldAnswer[];
} {
  for (const [index, answer] of answers.entries()) {
    const count = headcountOf(answer);
    if (count !== null) {
      return { headcount: count, otherAnswers: answers.filter((_, at) => at !== index) };
    }
  }
  return { headcount: null, otherAnswers: answers };
}

export function ReservationQueueRow({
  reservation,
  onConfirm,
  onReject,
  isAnswering,
}: ReservationQueueRowProps) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const deadlineStatus = reservation.confirmDeadline
    ? confirmationDeadlineStatus(reservation.confirmDeadline)
    : null;

  const { headcount, otherAnswers } = splitOffHeadcount(reservation.customAnswers);

  return (
    <li className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 space-y-1">
        <p className="font-medium truncate">
          {reservation.customerName ?? "Zákazník"}
          {headcount !== null ? ` · ${headcount} ${czechPlural(headcount, personForms)}` : ""}
        </p>
        <p className="text-sm text-muted-foreground">
          {reservation.activityName}
          {reservation.startTime ? ` · ${formatDateTime(reservation.startTime)}` : ""}
          {` · ${formatPrice(reservation.amountMinor)}`}
        </p>
        {reservation.confirmDeadline && deadlineStatus && (
          <ConfirmationDeadline deadline={reservation.confirmDeadline} status={deadlineStatus} />
        )}
        {otherAnswers.length > 0 && <CustomFieldAnswers answers={otherAnswers} />}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <LGoButton
          variant="ghost"
          iconLeft={<X />}
          onClick={() => setRejectOpen(true)}
          disabled={isAnswering}
        >
          Odmítnout
        </LGoButton>
        <LGoButton
          iconLeft={<Check />}
          onClick={() => onConfirm(reservation.id)}
          disabled={isAnswering}
        >
          Potvrdit
        </LGoButton>
      </div>

      <RejectReservationDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        activityName={reservation.activityName}
        isRejecting={isAnswering}
        onConfirm={() => onReject(reservation.id, { onSettled: () => setRejectOpen(false) })}
      />
    </li>
  );
}
